import { cn } from '@/lib/utils';

interface StrikeLeg {
  strikePrice: number;
  optionType: 'call' | 'put';
  action: 'BTO' | 'STO' | 'BTC' | 'STC';
}

interface StrikeVisualizationProps {
  legs: StrikeLeg[];
  currentPrice: number | null;
  className?: string;
}

/**
 * Graphical visualization of strike prices relative to current stock price
 * Shows a horizontal bar with strike markers and current price indicator
 */
export function StrikeVisualization({ legs, currentPrice, className }: StrikeVisualizationProps) {
  if (legs.length === 0) return null;

  // Get all unique strikes sorted
  const strikes = [...new Set(legs.map(l => l.strikePrice))].sort((a, b) => a - b);

  // Include current price in the range calculation
  const allPrices = currentPrice !== null
    ? [...strikes, currentPrice]
    : strikes;
  const minPrice = Math.min(...allPrices);
  const maxPrice = Math.max(...allPrices);

  // Add padding to the range (15% on each side)
  const range = maxPrice - minPrice;
  const padding = Math.max(range * 0.15, 2); // At least $2 padding
  const rangeMin = minPrice - padding;
  const rangeMax = maxPrice + padding;
  const totalRange = rangeMax - rangeMin;

  // Calculate position as percentage
  const getPosition = (price: number) => {
    return ((price - rangeMin) / totalRange) * 100;
  };

  // Determine if current price is in the "safe zone" (between short strikes for iron condor)
  const shortStrikes = legs
    .filter(l => l.action === 'STO' || l.action === 'STC')
    .map(l => l.strikePrice)
    .sort((a, b) => a - b);

  const isInSafeZone = currentPrice !== null &&
    shortStrikes.length >= 2 &&
    currentPrice > shortStrikes[0] &&
    currentPrice < shortStrikes[shortStrikes.length - 1];

  return (
    <div className={cn('py-3', className)}>
      {/* Scale markers */}
      <div className="flex justify-between text-xs text-gray-400 mb-1 px-1">
        <span>${rangeMin.toFixed(0)}</span>
        <span>${rangeMax.toFixed(0)}</span>
      </div>

      {/* Main visualization bar */}
      <div className="relative h-10 bg-gray-100 rounded-lg overflow-visible">
        {/* Safe zone highlight (between short strikes) */}
        {shortStrikes.length >= 2 && (
          <div
            className="absolute top-0 bottom-0 bg-green-100"
            style={{
              left: `${getPosition(shortStrikes[0])}%`,
              right: `${100 - getPosition(shortStrikes[shortStrikes.length - 1])}%`,
            }}
          />
        )}

        {/* Strike markers */}
        {legs.map((leg, index) => {
          const position = getPosition(leg.strikePrice);
          const isSell = leg.action === 'STO' || leg.action === 'STC';
          const isPut = leg.optionType === 'put';

          return (
            <div
              key={`${leg.strikePrice}-${leg.optionType}-${index}`}
              className="absolute top-0 bottom-0 flex flex-col items-center"
              style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
            >
              {/* Strike line */}
              <div
                className={cn(
                  'w-0.5 h-full',
                  isSell ? 'bg-green-500' : 'bg-red-400'
                )}
              />
              {/* Strike label */}
              <div
                className={cn(
                  'absolute text-xs font-medium whitespace-nowrap',
                  isPut ? 'bottom-full mb-1' : 'top-full mt-1',
                  isSell ? 'text-green-700' : 'text-red-600'
                )}
              >
                ${leg.strikePrice}
                <span className={cn(
                  'ml-0.5 text-[10px]',
                  isPut ? 'text-purple-600' : 'text-blue-600'
                )}>
                  {isPut ? 'P' : 'C'}
                </span>
              </div>
            </div>
          );
        })}

        {/* Current price marker */}
        {currentPrice !== null && (
          <div
            className="absolute top-0 bottom-0 flex flex-col items-center z-10"
            style={{ left: `${getPosition(currentPrice)}%`, transform: 'translateX(-50%)' }}
          >
            {/* Price indicator triangle */}
            <div className={cn(
              'w-0 h-0 border-l-[6px] border-r-[6px] border-b-[8px] border-l-transparent border-r-transparent',
              isInSafeZone ? 'border-b-green-600' : 'border-b-gray-800'
            )} />
            {/* Price line */}
            <div className={cn(
              'w-0.5 flex-1',
              isInSafeZone ? 'bg-green-600' : 'bg-gray-800'
            )} />
            {/* Price label */}
            <div className={cn(
              'absolute top-full mt-3 px-1.5 py-0.5 rounded text-xs font-bold',
              isInSafeZone ? 'bg-green-600 text-white' : 'bg-gray-800 text-white'
            )}>
              ${currentPrice.toFixed(2)}
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-10 text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 bg-green-500 rounded-full" />
          <span>Short (STO)</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 bg-red-400 rounded-full" />
          <span>Long (BTO)</span>
        </div>
        {currentPrice !== null && (
          <div className="flex items-center gap-1">
            <div className={cn(
              'w-2 h-2 rounded-full',
              isInSafeZone ? 'bg-green-600' : 'bg-gray-800'
            )} />
            <span>Current Price</span>
          </div>
        )}
      </div>
    </div>
  );
}
